import React, { useState } from 'react';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useToast } from 'use-toast-mui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { cancelBulkPointBatch, getBulkPointBatches } from '../../../api/member-point';
import { errorMessages } from '../../../utils/methods';
import CommonModalLayout from '../../../form-components/CommonModalLayout';
import CommonModalButton from '../../../form-components/CommonModalButton';
import ConfirmModal from '../../common/modal/ConfirmModal';

const headSx = { fontSize: 12, fontWeight: 700, color: '#6E747E', padding: '10px 12px', borderBottom: '1px solid #E5E7EB', textAlign: 'left', whiteSpace: 'nowrap' };
const cellSx = { fontSize: 13, color: '#2B2D31', padding: '12px', borderBottom: '1px solid #F0F1F4', verticalAlign: 'top' };

/**
 * 일괄 지급 내역 — 묶음(batchId) 단위 목록과 취소.
 * 취소는 회원마다 그 지급분의 잔여만 회수한다(이미 쓴 분은 회수 안 됨). 잔여 0·이미 취소된 회원은 건너뛴다.
 */
const BulkBatchListModal = ({ open, onClose, branchId, token }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [target, setTarget] = useState(null);

  const { data: batches = [], isLoading } = useQuery({
    queryKey: ['bulkPointBatches', branchId],
    queryFn: () => getBulkPointBatches(branchId, token),
    enabled: open && !!branchId && !!token,
  });

  const cancelMutation = useMutation({
    mutationFn: batchId => cancelBulkPointBatch(branchId, batchId, null, token),
    onSuccess: result => {
      toast.success(`${result?.memberCount || 0}${t('명')} · ${Number(result?.reclaimedPoint || 0).toLocaleString()}P ${t('회수했습니다')}`);
      queryClient.invalidateQueries({ queryKey: ['bulkPointBatches', branchId] });
      queryClient.invalidateQueries({ queryKey: ['branchPointHistories', branchId] });
    },
    onError: error => toast.error(errorMessages(error)),
  });

  return (
    <>
      <CommonModalLayout
        open={open}
        onClose={onClose}
        title={t('일괄 지급/차감 내역')}
        width={860}
        footer={
          <CommonModalButton variant="cancel" onClick={onClose}>
            {t('닫기')}
          </CommonModalButton>
        }>
        <Box sx={{ fontSize: 12, color: '#8A8F98', mb: '12px', lineHeight: 1.6 }}>
          {t('지급 취소는 각 회원에게 지급된 분 중 아직 남아 있는 포인트만 회수합니다(이미 사용한 포인트는 수기 차감으로). 차감 취소는 차감액 전액을 다시 적립합니다.')}
        </Box>
        <Box sx={{ maxHeight: 480, overflowY: 'auto', border: '1px solid #E5E7EB', borderRadius: '10px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={headSx}>{t('구분')}</th>
                <th style={headSx}>{t('일시')}</th>
                <th style={headSx}>{t('담당자')}</th>
                <th style={headSx}>{t('메모')}</th>
                <th style={{ ...headSx, textAlign: 'right' }}>{t('1인당')}</th>
                <th style={{ ...headSx, textAlign: 'right' }}>{t('인원')}</th>
                <th style={{ ...headSx, textAlign: 'right' }}>{t('합계')}</th>
                <th style={{ ...headSx, textAlign: 'right' }}>{t('잔여 합계')}</th>
                <th style={headSx} />
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr><td colSpan={9} style={{ ...cellSx, textAlign: 'center', color: '#9AA0A9' }}>{t('불러오는 중입니다')}</td></tr>
              )}
              {!isLoading && batches.length === 0 && (
                <tr><td colSpan={9} style={{ ...cellSx, textAlign: 'center', color: '#9AA0A9' }}>{t('일괄 지급 내역이 없습니다')}</td></tr>
              )}
              {batches.map(batch => {
                const isEarn = batch.type === 'EARN';
                const allCanceled = batch.canceledCount >= batch.memberCount;
                // 지급은 잔여가 있어야 회수할 게 있고, 차감은 미취소 회원이 있으면 복구할 게 있다
                const nothingLeft = isEarn ? !batch.remainPoint || batch.remainPoint <= 0 : allCanceled;
                return (
                  <tr key={batch.batchId}>
                    <td style={{ ...cellSx, whiteSpace: 'nowrap' }}>
                      <Box component="span" sx={{ px: '8px', py: '3px', borderRadius: '4px', fontSize: 12, fontWeight: 700, color: '#fff', background: isEarn ? '#4AB3BC' : '#F1685A' }}>
                        {isEarn ? t('지급') : t('차감')}
                      </Box>
                    </td>
                    <td style={{ ...cellSx, whiteSpace: 'nowrap' }}>{batch.regDate}</td>
                    <td style={cellSx}>{batch.staffName || '-'}</td>
                    <td style={cellSx}>{batch.memo || '-'}</td>
                    <td style={{ ...cellSx, textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {batch.pointPerMember == null ? t('전액') : `${Number(batch.pointPerMember).toLocaleString()} P`}
                    </td>
                    <td style={{ ...cellSx, textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {batch.memberCount}{t('명')}
                    </td>
                    <td style={{ ...cellSx, textAlign: 'right', whiteSpace: 'nowrap' }}>{Number(batch.totalPoint || 0).toLocaleString()} P</td>
                    <td style={{ ...cellSx, textAlign: 'right', whiteSpace: 'nowrap', color: nothingLeft ? '#9AA0A9' : '#0F7F85', fontWeight: 700 }}>
                      {isEarn ? `${Number(batch.remainPoint || 0).toLocaleString()} P` : '-'}
                    </td>
                    <td style={{ ...cellSx, whiteSpace: 'nowrap' }}>
                      {allCanceled ? (
                        <Box sx={{ fontSize: 11, fontWeight: 700, color: '#F1685A' }}>{t('취소됨')}</Box>
                      ) : (
                        <Box
                          component="button"
                          type="button"
                          disabled={nothingLeft || cancelMutation.isPending}
                          onClick={() => setTarget(batch)}
                          sx={{ height: 28, px: '10px', borderRadius: '6px', border: '1px solid #F5B5AE', background: '#FFF6F5', color: '#E0483A', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', '&:disabled': { opacity: 0.45, cursor: 'default' } }}>
                          {t('일괄 취소')}
                        </Box>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Box>
      </CommonModalLayout>

      <ConfirmModal
        open={!!target}
        onClose={() => setTarget(null)}
        onConfirm={() => {
          const batchId = target?.batchId;
          setTarget(null);
          if (batchId) cancelMutation.mutate(batchId);
        }}
        message={
          target?.type === 'EARN'
            ? `${target?.memberCount || 0}명에게 지급한 ${target?.pointPerMember == null ? '' : `${Number(target.pointPerMember).toLocaleString()}P `}중 남아 있는 ${Number(target?.remainPoint || 0).toLocaleString()}P를 회수합니다. 이미 사용한 포인트는 회수되지 않습니다. 일괄 지급을 취소할까요?`
            : `${target?.memberCount || 0}명에게서 차감한 ${Number(target?.totalPoint || 0).toLocaleString()}P를 전액 다시 적립합니다(이미 취소된 회원 제외). 일괄 차감을 취소할까요?`
        }
        highlightText="취소"
        cancelText="닫기"
        confirmText="일괄 취소"
      />
    </>
  );
};

export default BulkBatchListModal;
